// src/components/sections/ValuePropositionSection.tsx
import React, { useState } from 'react';
import {
  Edit2,
  ChevronDown,
  ChevronUp,
  Users,
  Lightbulb,
  Sparkles,
  ListChecks,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { AgentData_V10, AgentHubPageViewMode, BenefitItemV10 } from '@dopomogai/types';
import { Card } from '@dopomogai/ui';
import {Button} from '@dopomogai/ui';
import { useTranslation } from 'react-i18next';
import { renderIcon } from '@dopomogai/utils';
import clsx from 'clsx';
import {
  INNER_CARD_ANIMATE_RESTING,
  INNER_CARD_HOVER,
  INNER_CARD_TRANSITION_HOVER,
} from '../../shared/animationConstants';

interface ValuePropositionSectionProps {
  agentData: AgentData_V10;
  viewMode: AgentHubPageViewMode;
  onAction: (actionType: string, payload?: any) => void;
  headerBgColor?: string;
  id?: string;
}

const listItemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: {
      delay: i * 0.08,
      duration: 0.3,
      ease: "easeOut"
    }
  })
};

const sectionContentVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: "easeOut"
    }
  }
};

export const ValuePropositionSection = React.forwardRef<HTMLDivElement, ValuePropositionSectionProps>(
  ({ agentData, viewMode, onAction, headerBgColor, id }, ref) => {
    const { t } = useTranslation('agent-common');
    const [isExpanded, setIsExpanded] = useState(false);
    const isOwnerView = viewMode === 'owner_dashboard';
    const { valuePropositionSection } = agentData;

    if (!valuePropositionSection) {
      if (isOwnerView) {
        return (
          <Card id={id} ref={ref} titleKey="valuePropositionSection.addSectionTitle" headerBgColor={headerBgColor}>
            <div className="text-center py-10">
              <Lightbulb size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500 mb-4">{t('valuePropositionSection.noContentOwner')}</p>
              <Button
                textKey="valuePropositionSection.configureButton"
                onClick={() => onAction('EDIT_VALUE_PROPOSITION', null)}
              />
            </div>
          </Card>
        );
      }
      return null;
    }

    const { title, solutionStatement, expandableContent, keyBenefits, targetAudienceDescription, primaryUseCases } = valuePropositionSection;

    return (
      <Card
        id={id}
        ref={ref}
        title={title || t('valuePropositionSection.defaultTitle')}
        icon={<Lightbulb size={22} className="text-emerald-600" />}
        headerBgColor={headerBgColor}
        actionButton={
          isOwnerView && (
            <Button
              textKey="common.editSection"
              variant="outline"
              size="sm"
              icon={<Edit2 size={14} />}
              onClick={() => onAction('EDIT_VALUE_PROPOSITION', null)}
            />
          )
        }
      >
        <div className="space-y-10 md:space-y-12">
          {solutionStatement && (
            <motion.div variants={sectionContentVariants} initial="hidden" animate="visible">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3 flex items-center">
                <Lightbulb size={28} className="mr-3 text-amber-500" />
                {t('valuePropositionSection.solutionTitle')}
              </h2>
              <p className="text-gray-700 text-lg md:text-xl leading-relaxed">
                {solutionStatement}
              </p>

              {expandableContent && (
                <>
                  <AnimatePresence initial={false}>
                    {isExpanded && (
                      <motion.div
                        key="expandableContent"
                        initial="collapsed"
                        animate="open"
                        exit="collapsed"
                        variants={{
                          open: { opacity: 1, height: 'auto', marginTop: '1.25rem' },
                          collapsed: { opacity: 0, height: 0, marginTop: '0rem' },
                        }}
                        transition={{ duration: 0.35, ease: [0.04, 0.62, 0.23, 0.98] }}
                        className="overflow-hidden"
                      >
                        <div className="prose prose-base sm:prose-lg max-w-none text-gray-600 leading-relaxed border-l-4 border-emerald-500 pl-6 py-3 my-2 bg-emerald-50/60 rounded-r-md">
                          <p>{expandableContent}</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  <Button
                    variant="ghost"
                    size="sm"
                    textKey={isExpanded ? 'valuePropositionSection.readLess' : 'valuePropositionSection.readMore'}
                    icon={isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    iconPosition="right"
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="mt-4 text-emerald-600 hover:text-emerald-700 font-medium"
                  />
                </>
              )}
            </motion.div>
          )}

          {keyBenefits && keyBenefits.length > 0 && (
            <motion.div variants={sectionContentVariants} initial="hidden" animate="visible" custom={1}>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4 flex items-center">
                <Sparkles size={28} className="mr-3 text-teal-500" />
                {t('valuePropositionSection.keyBenefitsTitle')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {keyBenefits.map((benefit: BenefitItemV10, index: number) => (
                  <motion.div
                    key={`benefit-${index}`}
                    custom={index}
                    variants={listItemVariants}
                    initial="hidden" 
                    whileInView="visible" 
                    viewport={{ once: true, amount: 0.3 }}
                    animate={INNER_CARD_ANIMATE_RESTING} 
                    whileHover={INNER_CARD_HOVER}
                    transition={INNER_CARD_TRANSITION_HOVER}
                    className={clsx(
                      "bg-white p-5 rounded-xl border-2 border-black flex items-start gap-4 cursor-default"
                    )}
                  >
                    <div className="flex-shrink-0 bg-teal-100 text-teal-600 p-2.5 rounded-full border-2 border-teal-300 shadow-sm mt-1">
                      {renderIcon(benefit.icon || 'checkcircle', { size: 20 })}
                    </div>
                    <p className="text-gray-700 font-medium leading-relaxed">{benefit.text}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {(targetAudienceDescription || (primaryUseCases && primaryUseCases.length > 0)) && (
            <motion.div variants={sectionContentVariants} initial="hidden" animate="visible" custom={2}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-10">
                {targetAudienceDescription && (
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4 flex items-center">
                        <Users size={28} className="mr-3 text-sky-500"/>
                        {t('valuePropositionSection.targetAudienceTitle')}
                    </h2>
                    {/* === THIS IS THE UPDATED ELEMENT === */}
                    <motion.div
                       animate={INNER_CARD_ANIMATE_RESTING}
                       whileHover={{
                           x: -2,
                           y: -2,
                           boxShadow: "2px 2px 0px 0px #0ea5e9" // Tailwind's sky-500
                       }}
                       transition={INNER_CARD_TRANSITION_HOVER} 
                       className={clsx(
                        "bg-sky-50 rounded-xl p-6 border-2 border-black text-sky-800 cursor-default"
                       )}
                    >
                        <p className="leading-relaxed text-sky-700">
                          {targetAudienceDescription}
                        </p>
                    </motion.div>
                  </div>
                )}

                {primaryUseCases && primaryUseCases.length > 0 && (
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4 flex items-center">
                        <ListChecks size={28} className="mr-3 text-indigo-500"/>
                        {t('valuePropositionSection.primaryUseCasesTitle')}
                    </h2>
                    <div className="space-y-4">
                      {primaryUseCases.map((useCase: string, index: number) => (
                        <motion.div
                          key={`usecase-${index}`}
                          custom={index}
                          variants={listItemVariants}
                          initial="hidden"
                          whileInView="visible"
                          viewport={{ once: true, amount: 0.5 }}
                          animate={INNER_CARD_ANIMATE_RESTING}
                          whileHover={INNER_CARD_HOVER}
                          transition={INNER_CARD_TRANSITION_HOVER}
                          className={clsx(
                            "bg-white p-4 rounded-xl border-2 border-black flex items-center gap-3 cursor-default"
                          )}
                        >
                          <div className="flex-shrink-0 bg-indigo-500 text-white rounded-md w-8 h-8 flex items-center justify-center font-bold text-sm border-2 border-black shadow-sm">
                              {index + 1}
                          </div>
                          <p className="text-indigo-800 font-medium">{useCase}</p>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </Card>
    );
  }
);

ValuePropositionSection.displayName = 'ValuePropositionSection';
